import {
  BrowserSideBlocController,
  provideBrowserSideBlocController
} from '@bcf-vanilla-ts-v1-platforms/platform-worker/browser/browser-side-bloc-controller';
import { TypedSubject } from '@bcf-vanilla-ts-v1-platforms/platform-worker/browser/types';
import { ExtractedObservable, WorkerBlocChannels } from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/types';
import { WorkerBlocId } from '@bcf-vanilla-ts-v1-platforms/platform-worker/worker-blocs.type';
import { MonoTypeOperatorFunction, Observable, Subscription, asapScheduler, tap } from 'rxjs';
import { UnsubscribableCtrl } from './unsubscribable-ctrl';

type AfterPropSetHook = {
  prop: any;
  cb: () => void;
};

// Decision: see ADR-0002
export class UnsubscribableCtrlWithWorkerBloc<T, R extends WorkerBlocChannels> extends UnsubscribableCtrl<T> {
  private _uniqueId!: string;
  private _workerBlocId!: WorkerBlocId;

  private _browserSideBlocController: BrowserSideBlocController<R> = provideBrowserSideBlocController();

  // FOR UI READY NOTIFIER
  private _readyWhenPropsSet: Array<keyof R['rx']> = [];
  private _readyWhenPropsReceived: Record<string, boolean | undefined> = {};
  private _isReady: boolean = false;

  private _afterPropSetHooks: AfterPropSetHook[] = [];

  protected _initWorkerBloc(workerBlocId: WorkerBlocId): void {
    this._workerBlocId = workerBlocId;
    this._uniqueId = BrowserSideBlocController.getId(workerBlocId);
    this._browserSideBlocController.sendCreateInstance(workerBlocId, this._uniqueId);
    asapScheduler.schedule(() => this._host.requestUpdate(), 1);
    this._sub = this._browserSideBlocController
      .workerBlocInitialized(this._workerBlocId, this._uniqueId)
      .subscribe(() => this._initializedBloc());
  }

  public init(_arg1?: unknown, _arg2?: unknown, _arg4?: unknown): void {}

  public afterPropSetHook(prop: keyof R['rx'], cb: () => void): void {
    this._afterPropSetHooks.push({
      prop: prop,
      cb: cb
    });
  }

  protected _initializedBloc(): void {}

  protected _setupReadyWhenPropsReceived(props: Array<keyof R['rx']>): void {
    this._readyWhenPropsSet = props;
  }

  private _checkUiReady(): void {
    if (this._isReady) {
      return;
    }
    if (this._readyWhenPropsSet.length === 0) {
      return;
    }
    const allIsReady: boolean = this._readyWhenPropsSet.every(
      (key: keyof R['rx']) => this._readyWhenPropsReceived[key as string]
    );
    if (allIsReady) {
      this._isReady = true;
    }
  }

  protected _tapAndSetReadyProp<T>(key: keyof R['rx']): MonoTypeOperatorFunction<T> {
    return tap(() => {
      this._readyWhenPropsReceived[key as string] = true;
      this._checkUiReady();
    });
  }

  protected override _set<K extends keyof T>(key: K) {
    return <V extends T[K]>(value: V): void => {
      const that: T = this as any as T;
      that[key] = value;
      this._host.requestUpdate();
      this._readyWhenPropsReceived[key as string] = true;
      const afterPropSetHook: AfterPropSetHook | undefined = this._afterPropSetHooks.find(
        (data: AfterPropSetHook) => data.prop === key
      );
      afterPropSetHook?.cb();
      this._checkUiReady();
    };
  }

  protected _setResolvedPromise<K extends keyof T, V extends T[K]>(key: K, promise: Promise<V>): void {
    promise.then((value: V) => {
      const that: T = this as any as T;
      that[key] = value;
      this._host.requestUpdate();
      this._readyWhenPropsReceived[key as string] = true;
      this._checkUiReady();
    });
  }

  protected _subTo<K extends keyof R['rx'], V extends ExtractedObservable<R['rx'][K]>>(channelRx: K): Observable<V> {
    return this._browserSideBlocController.subTo(this._workerBlocId, this._uniqueId, channelRx);
  }

  protected _subToAndSubscribe<K extends keyof R['rx'], Z extends keyof T, V extends ExtractedObservable<R['rx'][K]>>(
    channelRx: K & Z
  ): Subscription {
    // TODO: fix any
    return this._subTo<K, V>(channelRx).subscribe(this._set(channelRx as any));
  }

  protected _workerSubject<V extends TypedSubject<R['subjects'][K]>, K extends keyof R['subjects']>(subjectKey: K): V {
    return this._browserSideBlocController.workerSubject(this._workerBlocId, () => this._uniqueId, subjectKey);
  }

  protected _workerObservable<K extends keyof R['rx'], V extends ExtractedObservable<R['rx'][K]>>(
    channelRx: K
  ): Observable<V> {
    return this._browserSideBlocController.subTo(this._workerBlocId, this._uniqueId, channelRx);
  }

  public override hostDisconnected(): void {
    super.hostDisconnected();
    this._browserSideBlocController.sendDestroyInstance(this._workerBlocId, this._uniqueId);
  }
}
